import {
  SelectMasterEntity,
  SelectMasterResponse,
  SelectResponse,
  WriteSelectMasterFormEntity,
  BulkUpdateSelectMasterFormEntity,
  BulkUpdateSelectMastersRequest,
} from 'src/types'

type ToEntityWithOutOptionsInput = {
  projectId: string
  response: SelectMasterResponse | SelectResponse
}

type ToEntityInput = {
  projectId: string
  response: SelectMasterResponse
}
type ToEntitiesInput = {
  projectId: string
  responses: SelectMasterResponse[]
}

export const SelectMasterTranslator = {
  toRequest: (formEntity: BulkUpdateSelectMasterFormEntity): BulkUpdateSelectMastersRequest => {
    const filteredItems = formEntity.items.filter(item => {
      // entityIdがなくisDeletedがtrueの場合は、編集モードに移行後新たに作成しようとしたが保存前に削除したものになるため、APIに送信する必要がない
      return !(!item.entityId && !!item.isDeleted)
    })

    return {
      selectMasters: filteredItems.map(item => {
        return {
          id: item.entityId ? Number.parseInt(item.entityId) : undefined,
          displayName: item.displayName,
          name: item.name,
          description: item.description,
          modelRoomInfo: item.modelRoomInfo,
          annotation: item.annotation,
          isRemoveFile: item.isRemoveFile,
          file: item.fileBase64,
          fileName: item.fileName,
          priority: item.priority,
          isDelete: item.isDeleted ?? false,
          isPlanSelect: item.isPlanSelect,
          selectOptionMasters: item.options.map((option, index) => {
            return {
              id: option.entityId ? Number.parseInt(option.entityId) : undefined,
              name: option.name,
              annotation: option.annotation,
              isPaid: option.isPaid,
              file: option.fileBase64,
              fileName: option.fileName,
              priority: option.priority ?? index + 1,
              isRemoveFile: option.isRemoveFile,

              children:
                option.children.map((child, childIndex) => {
                  return {
                    id: child.entityId ? Number.parseInt(child.entityId) : undefined,
                    name: child.name,
                    annotation: child.annotation,
                    isPaid: child.isPaid,
                    file: child.fileBase64,
                    fileName: child.fileName,
                    priority: child.priority ?? childIndex + 1,
                    isRemoveFile: child.isRemoveFile,
                  }
                }) ?? [],
            }
          }),
        }
      }),
    }
  },

  toEntityWithOutOptions: ({
    response,
    projectId,
  }: ToEntityWithOutOptionsInput): Omit<SelectMasterEntity, 'options' | 'roomIds'> => {
    return {
      id: `${response.id}`,
      projectId,
      name: response.name,
      displayName: response.displayName,
      priority: response.priority,
      description: response.description ?? undefined,
      modelRoomInfo: response.modelRoomInfo ?? undefined,
      annotation: response.annotation ?? undefined,
      isPlanSelect: response.isPlanSelect,
      fileUrl: response.file?.url && response.file?.url !== '' ? response.file.url : undefined,
      fileName: 'fileName' in response ? response.fileName ?? undefined : undefined,
    }
  },

  toEntityOptions: (
    response: SelectMasterResponse | SelectResponse
  ): SelectMasterEntity['options'] => {
    const options =
      'selectOptionMasters' in response ? response.selectOptionMasters : response.selectOptions

    return options.map(option => {
      return {
        id: `${option.id}`,
        name: option.name,
        annotation: option.annotation ?? undefined,
        isPaid: option.isPaid,
        isSelected: 'isSelected' in option ? option.isSelected : undefined,
        fileUrl: option.file?.url && option.file?.url !== '' ? option.file?.url : undefined,
        fileName: 'fileName' in option ? option.fileName ?? undefined : undefined,
        children: option.children
          ? option.children.map(child => {
              return {
                id: `${child.id}`,
                name: child.name,
                annotation: child.annotation ?? undefined,
                isPaid: child.isPaid,
                isSelected: 'isSelected' in child ? child.isSelected : undefined,
                fileUrl: child.file?.url && child.file?.url !== '' ? child.file?.url : undefined,
                fileName: 'fileName' in child ? child.fileName ?? undefined : undefined,
              }
            })
          : undefined,
      }
    })
  },

  toEntity: ({ response, projectId }: ToEntityInput): SelectMasterEntity => {
    return {
      ...SelectMasterTranslator.toEntityWithOutOptions({
        response,
        projectId,
      }),
      options: SelectMasterTranslator.toEntityOptions(response),
      roomIds: response.roomIds,
    }
  },

  toPartialEntity: (formEntity: WriteSelectMasterFormEntity): Partial<SelectMasterEntity> => {
    return {
      displayName: formEntity.displayName,
      name: formEntity.name,
      priority: formEntity.priority,
      description: formEntity.description,
      modelRoomInfo: formEntity.modelRoomInfo,
      annotation: formEntity.annotation,
      isPlanSelect: formEntity.isPlanSelect,
      fileUrl: formEntity.fileUrl ?? undefined,
      options: formEntity.options.map(option => {
        return {
          id: `${option.entityId}`,
          name: option.name,
          annotation: option.annotation ?? undefined,
          isPaid: option.isPaid,
          priority: option.priority,
          fileUrl: option.fileUrl ?? undefined,
          children: option.children
            ? option.children.map(child => {
                return {
                  id: `${child.entityId}`,
                  name: child.name,
                  annotation: child.annotation ?? undefined,
                  isPaid: child.isPaid,
                  priority: child.priority,
                  fileUrl: child.fileUrl ?? undefined,
                }
              })
            : undefined,
        }
      }),
    }
  },

  toEntities: ({ projectId, responses }: ToEntitiesInput): SelectMasterEntity[] => {
    return responses.map(response => SelectMasterTranslator.toEntity({ projectId, response }))
  },

  toFormEntity: (entity: SelectMasterEntity): WriteSelectMasterFormEntity => {
    return {
      entityId: entity.id,
      displayName: entity.displayName,
      name: entity.name,
      description: entity.description ?? undefined,
      modelRoomInfo: entity.modelRoomInfo ?? undefined,
      annotation: entity.annotation ?? undefined,
      isRemoveFile: false,
      priority: entity.priority,
      fileUrl: entity.fileUrl ?? undefined,
      fileName: entity.fileName ?? undefined,
      isDeleted: false,
      isPlanSelect: entity.isPlanSelect,
      options: entity.options.map(option => {
        return {
          id: option.id,
          entityId: option.id,
          name: option.name,
          annotation: option.annotation ?? undefined,
          isPaid: option.isPaid,
          fileUrl: option.fileUrl ?? undefined,
          fileName: option.fileName ?? undefined,
          priority: option.priority,
          children:
            option.children?.map(child => {
              return {
                id: child.id,
                entityId: child.id,
                name: child.name,
                annotation: child.annotation ?? undefined,
                isPaid: child.isPaid,
                fileUrl: child.fileUrl ?? undefined,
                fileName: child.fileName ?? undefined,
                priority: child.priority,
              }
            }) ?? [],
        }
      }),
      roomIds: entity.roomIds,
    }
  },
} as const
